import { ChangeEvent, MouseEvent, ReactElement, useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { distanceXY, projectPointOntoWallAxisCartesianAware } from '@geometry'
import { isNull } from 'lodash-es'
import BorderLeftIcon from '@mui/icons-material/BorderLeft'
import BorderRightIcon from '@mui/icons-material/BorderRight'
import HeightIcon from '@mui/icons-material/Height'
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule'
import WidthWide from '@mui/icons-material/WidthWide'
import {
  Divider,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { CoordinatePointInput, XYCopyButton } from '@ui/forms'
import { NUM_DECIMALS_ROUND_FLOATS, roundFloatToDecimalPlaces } from 'src/geometry/floatHacks'
import { changeOpeningWidthCartesianAware, WidthAnchorMode } from './utils'

interface Props {
  isEditable: boolean
  wall: ShapeObject
  mode?: '2D' | '3D'
}

const FormFields = ({ wall, mode }: Props): ReactElement => {
  const { getValues, setValue } = useFormContext()
  const openingWidth = useWatch({ name: 'openingWidth' }) as number | string
  const openingHeight = useWatch({ name: 'openingHeight' }) as number | string
  const openingWidthAnchorMode =
    (useWatch({ name: 'openingWidthAnchorMode' }) as WidthAnchorMode | undefined) ?? 'start'

  const handleReplaceStart = useCallback(
    (x: number, y: number, z: number) => {
      if (!wall) return
      const { globalStart, globalEnd } = getValues()
      if (!globalStart || !globalEnd) return

      // hack: the z component can be reused from the global start point
      const newStartProjectedIntoWall = projectPointOntoWallAxisCartesianAware(
        wall,
        new ImmutableVector3(x, y, z),
      )
      setValue(
        'globalStart',
        {
          x: newStartProjectedIntoWall.x,
          y: newStartProjectedIntoWall.y,
          z: newStartProjectedIntoWall.z,
        },
        { shouldDirty: true },
      )
      const newOpeningWidth = roundFloatToDecimalPlaces(
        distanceXY(newStartProjectedIntoWall, globalEnd),
        NUM_DECIMALS_ROUND_FLOATS,
      )
      setValue('openingWidth', newOpeningWidth, { shouldDirty: true })
      const newOpeningHeight = roundFloatToDecimalPlaces(
        globalEnd.z - newStartProjectedIntoWall.z,
        NUM_DECIMALS_ROUND_FLOATS,
      )
      setValue('openingHeight', newOpeningHeight, { shouldDirty: true })
    },
    [wall, setValue, getValues],
  )

  const handleReplaceEnd = useCallback(
    (x: number, y: number, z: number) => {
      if (!wall) return
      const { globalStart, globalEnd } = getValues()
      if (!globalStart || !globalEnd) return

      const newEndProjectedIntoWall = projectPointOntoWallAxisCartesianAware(
        wall,
        new ImmutableVector3(x, y, z),
      )
      setValue(
        'globalEnd',
        {
          x: newEndProjectedIntoWall.x,
          y: newEndProjectedIntoWall.y,
          z: newEndProjectedIntoWall.z,
        },
        { shouldDirty: true },
      )
      const newOpeningWidth = distanceXY(globalStart, newEndProjectedIntoWall)
      setValue('openingWidth', newOpeningWidth, { shouldDirty: true })
      const newOpeningHeight = newEndProjectedIntoWall.z - globalStart.z
      setValue('openingHeight', newOpeningHeight, { shouldDirty: true })
    },
    [wall, setValue, getValues],
  )

  const handleReplaceStartWithMode = useCallback(
    (x: number, y: number, z: number) => {
      if (mode === '2D') {
        const { globalStart } = getValues()
        const preservedZ = globalStart?.z
        handleReplaceStart(x, y, preservedZ ?? z)
        return
      }

      handleReplaceStart(x, y, z)
    },
    [mode, getValues, handleReplaceStart],
  )

  const handleReplaceEndWithMode = useCallback(
    (x: number, y: number, z: number) => {
      if (mode === '2D') {
        const { globalEnd } = getValues()
        const preservedZ = globalEnd?.z
        handleReplaceEnd(x, y, preservedZ ?? z)
        return
      }

      handleReplaceEnd(x, y, z)
    },
    [mode, getValues, handleReplaceEnd],
  )

  const handleChangeXStart = useCallback(
    (x: number | null) => {
      if (isNull(x)) return
      if (!wall) return
      const { globalStart } = getValues()
      if (!globalStart) return
      handleReplaceStart(x, globalStart.y, globalStart.z)
    },
    [handleReplaceStart, getValues, wall],
  )

  const handleChangeYStart = useCallback(
    (y: number | null) => {
      if (isNull(y)) return
      if (!wall) return
      const { globalStart } = getValues()
      if (!globalStart) return
      handleReplaceStart(globalStart.x, y, globalStart.z)
    },
    [handleReplaceStart, getValues, wall],
  )

  const handleChangeXEnd = useCallback(
    (x: number | null) => {
      if (isNull(x)) return
      if (!wall) return
      const { globalEnd } = getValues()
      if (!globalEnd) return
      handleReplaceEnd(x, globalEnd.y, globalEnd.z)
    },
    [handleReplaceEnd, getValues, wall],
  )

  const handleChangeYEnd = useCallback(
    (y: number | null) => {
      if (isNull(y)) return
      if (!wall) return
      const { globalEnd } = getValues()
      if (!globalEnd) return
      handleReplaceEnd(globalEnd.x, y, globalEnd.z)
    },
    [handleReplaceEnd, getValues, wall],
  )

  const handleChangeZStart = useCallback(
    (z: number | null) => {
      if (isNull(z)) return
      if (!wall) return
      const { globalStart } = getValues()
      if (!globalStart) return
      handleReplaceStart(globalStart.x, globalStart.y, z)
    },
    [handleReplaceStart, getValues, wall],
  )

  const handleChangeZEnd = useCallback(
    (z: number | null) => {
      if (isNull(z)) return
      if (!wall) return
      const { globalEnd } = getValues()
      if (!globalEnd) return
      handleReplaceEnd(globalEnd.x, globalEnd.y, z)
    },
    [handleReplaceEnd, getValues, wall],
  )

  const updateFormValues = useCallback(
    (newWidthAsNumber: number, openingWidthAnchorMode: WidthAnchorMode) => {
      if (Number.isFinite(newWidthAsNumber) && newWidthAsNumber > 0) {
        setValue('openingWidth', newWidthAsNumber, { shouldDirty: true })

        const { globalStart, globalEnd } = getValues()
        if (!globalStart || !globalEnd) return

        const { newStart, newEnd, newHeight } = changeOpeningWidthCartesianAware(
          globalStart,
          globalEnd,
          newWidthAsNumber,
          openingWidthAnchorMode,
        )
        setValue('globalStart', newStart, { shouldDirty: true })
        setValue('globalEnd', newEnd, { shouldDirty: true })
        setValue('openingHeight', newHeight, { shouldDirty: true })
      }
    },
    [setValue, getValues],
  )

  const handleChangeOpeningWidth = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const rawWidth = event.target.value
      setValue('openingWidth', rawWidth, { shouldDirty: true })

      const newWidthAsNumber = Number(rawWidth)

      updateFormValues(newWidthAsNumber, openingWidthAnchorMode)
    },
    [openingWidthAnchorMode, updateFormValues, setValue],
  )

  const handleChangeOpeningWidthAnchorMode = useCallback(
    (_event: MouseEvent<HTMLElement>, value: WidthAnchorMode | null) => {
      if (!value) return
      setValue('openingWidthAnchorMode', value)
      const openingWidthAsNumber = Number(openingWidth)
      if (!Number.isFinite(openingWidthAsNumber) || openingWidthAsNumber <= 0) return
      updateFormValues(openingWidthAsNumber, value)
    },
    [openingWidth, updateFormValues, setValue],
  )

  const handleChangeOpeningHeight = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const rawHeight = event.target.value
      setValue('openingHeight', rawHeight, { shouldDirty: true })

      const newHeightAsNumber = Number(rawHeight)
      if (!Number.isFinite(newHeightAsNumber) || newHeightAsNumber <= 0) return

      const { globalStart, globalEnd } = getValues()
      if (!globalStart || !globalEnd) return

      setValue(
        'globalEnd',
        { x: globalEnd.x, y: globalEnd.y, z: globalStart.z + newHeightAsNumber },
        { shouldDirty: true },
      )
    },
    [getValues, setValue],
  )

  return (
    <Stack
      spacing={1}
      border={1}
      p={1}
      borderColor="grey.200"
      borderRadius={1}
      sx={{ '& .MuiFormControlLabel-label': { ml: 0.5 } }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Stack direction="column" spacing={1} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <HeightIcon />
            <TextField
              label="neue Höhe"
              type="number"
              size="small"
              value={openingHeight ?? ''}
              onChange={handleChangeOpeningHeight}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                },
              }}
              inputProps={{
                inputMode: 'decimal',
                min: 0,
                step: 0.05,
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <HorizontalRuleIcon />
            <TextField
              label="neue Breite"
              type="number"
              size="small"
              value={openingWidth ?? ''}
              onChange={handleChangeOpeningWidth}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                },
              }}
              inputProps={{
                inputMode: 'decimal',
                min: 0,
                step: 0.05,
              }}
            />
          </Stack>
        </Stack>
        <ToggleButtonGroup
          value={openingWidthAnchorMode}
          onChange={handleChangeOpeningWidthAnchorMode}
          exclusive
          size="small"
          fullWidth
        >
          {/* note: the anchor mode end preserves the end but moves the start */}
          <ToggleButton value="end">
            <Stack direction="column" spacing={0.5} alignItems="center">
              <BorderLeftIcon sx={{ color: 'blue' }} />
              <Typography fontSize="small">Start versch.</Typography>
            </Stack>
          </ToggleButton>
          <ToggleButton value="middle">
            <Stack direction="column" spacing={0.5} alignItems="center">
              <WidthWide sx={{ color: 'blue' }} />
              <Typography fontSize="small">mittig</Typography>
            </Stack>
          </ToggleButton>
          {/* note: the anchor mode start preserves the start but moves the end */}
          <ToggleButton value="start">
            <Stack direction="column" spacing={0.5} alignItems="center">
              <BorderRightIcon sx={{ color: 'blue' }} />
              <Typography fontSize="small">Ende versch.</Typography>
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack spacing={0.75}>
        <Divider>Position 3D</Divider>
        <CoordinatePointInput
          label="Start"
          name="globalStart"
          onChangeX={handleChangeXStart}
          onChangeY={handleChangeYStart}
          onChangeZ={handleChangeZStart}
          onReplace={handleReplaceStartWithMode}
          pointManipulation={{
            x: { roundOnReplace: true, defaultRoundNumDecimalsOnReplace: 2 },
            y: { roundOnReplace: true, defaultRoundNumDecimalsOnReplace: 2 },
            z: { roundOnReplace: true, defaultRoundNumDecimalsOnReplace: 2 },
          }}
          actions={<XYCopyButton name="globalStart" />}
        />
        <CoordinatePointInput
          label="Ende"
          name="globalEnd"
          onChangeX={handleChangeXEnd}
          onChangeY={handleChangeYEnd}
          onChangeZ={handleChangeZEnd}
          onReplace={handleReplaceEndWithMode}
          pointManipulation={{
            x: { roundOnReplace: true, defaultRoundNumDecimalsOnReplace: 2 },
            y: { roundOnReplace: true, defaultRoundNumDecimalsOnReplace: 2 },
            z: { roundOnReplace: true, defaultRoundNumDecimalsOnReplace: 2 },
          }}
          actions={<XYCopyButton name="globalEnd" />}
        />
      </Stack>
    </Stack>
  )
}

export { FormFields }
